import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { bounds } from 'leaflet';
import { Box, Card, CardBody, Flex, Heading, Image, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import { Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';


const defaultCenter = { lat: 21.161908, lng: -86.8515279 };

// Fix the default icon issue in Leaflet 1.x
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Map = ({ centeredLocation, hotelCoords, selectedHotel: propSelectedHotel, setSelectedHotel: setPropSelectedHotel, searchTrigger }) => {
  const [shouldUpdateCenter, setShouldUpdateCenter] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const prevCenteredLocationRef = useRef(null);
  const prevSearchTriggerRef = useRef(0);

  // Use prop selectedHotel if provided, otherwise use internal state
  const [internalSelectedHotel, setInternalSelectedHotel] = useState(null);
  const selectedHotel = propSelectedHotel !== undefined ? propSelectedHotel : internalSelectedHotel;
  const setSelectedHotel = setPropSelectedHotel || setInternalSelectedHotel;

  const cppLabel = useBreakpointValue({ base: "CPP", md: "Cents Per Point" });

  const handleMarkerClick = (hotel) => {
    setSelectedHotel(hotel);
    setImageUrl(hotel.i);
    setShouldUpdateCenter(false); // Prevent center update after marker click
  };

  const ChangeView = ({ center }) => {
    const map = useMap();
    
    useEffect(() => {
      // Only update if center actually changed and we should update
      const centerStr = `${center.lat},${center.lng}`;
      const prevCenterStr = prevCenteredLocationRef.current 
        ? `${prevCenteredLocationRef.current.lat},${prevCenteredLocationRef.current.lng}` 
        : null;
      
      if (shouldUpdateCenter && centerStr !== prevCenterStr) {
        // Update ref AFTER flying to prevent duplicate updates
        prevCenteredLocationRef.current = center;
        // Don't clear selectedHotel when centering - it might be set from card click
        map.flyTo(center, 10);
      }
    }, [center, shouldUpdateCenter, map]);
  
    return null;
  };
  
  useEffect(() => {
    // Only allow center update when centeredLocation actually changes to a different value
    const currentStr = `${centeredLocation.lat},${centeredLocation.lng}`;
    const prevStr = prevCenteredLocationRef.current 
      ? `${prevCenteredLocationRef.current.lat},${prevCenteredLocationRef.current.lng}` 
      : null;
    
    if (currentStr !== prevStr) {
      // Always allow center update when city changes, even if a hotel was selected
      // This ensures the map flies to the new city location
      setShouldUpdateCenter(true);
    }
  }, [centeredLocation]);

  // Force map to fly when search is triggered (even if location is the same)
  useEffect(() => {
    if (searchTrigger !== undefined && searchTrigger !== prevSearchTriggerRef.current) {
      prevSearchTriggerRef.current = searchTrigger;
      // Reset the ref to force a fly even if location hasn't changed
      prevCenteredLocationRef.current = null;
      setShouldUpdateCenter(true);
    }
  }, [searchTrigger]);

  // Component to center map on selected hotel when it's set from props
  const CenterOnSelectedHotel = ({ hotel }) => {
    const map = useMap();
    
    useEffect(() => {
      if (hotel && hotel.lat && hotel.lng) {
        setShouldUpdateCenter(false); // Prevent auto-center updates when showing specific hotel
        map.flyTo([hotel.lat, hotel.lng], 13); // Zoom in closer when showing a specific hotel
      } else if (!hotel) {
        // When hotel is cleared, allow center updates again
        setShouldUpdateCenter(true);
      }
    }, [hotel, map]);
    
    return null;
  };

  // Handle selectedHotel from props (when card is clicked)
  useEffect(() => {
    if (propSelectedHotel && propSelectedHotel !== undefined) {
      setImageUrl(propSelectedHotel.i || 'https://maxmypoint.com/default-hotel-image-small.jpg');
    }
  }, [propSelectedHotel]);


  // console.log(`map centeredLocation: ${JSON.stringify(centeredLocation)}`)
  return (
    <Box flexGrow='1' align='stretch' height="100vh">
      <MapContainer
        style={{ height: '100%', width: '100%' }}
        center={centeredLocation && Object.keys(centeredLocation).length !== 0 ? centeredLocation : defaultCenter}
        zoom={10}
        scrollWheelZoom={true}
      >
        <ChangeView center={centeredLocation && Object.keys(centeredLocation).length !== 0 ? centeredLocation : defaultCenter} />
        <CenterOnSelectedHotel hotel={propSelectedHotel} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {hotelCoords.map((hotel, index) => {
          // console.log(`hotel: ${JSON.stringify(hotel)}`);
          
          // Safe display logic for marker text
          const getMarkerText = () => {
            if (hotel.points && typeof hotel.points === 'number' && !isNaN(hotel.points)) {
              return (hotel.points / 1000).toFixed(1) + 'K';
            } else if (hotel.price && typeof hotel.price === 'number' && !isNaN(hotel.price)) {
              return '$' + Math.round(hotel.price).toLocaleString();
            } else {
              return 'N/A';
            }
          };
          
          const customIcon = new L.DivIcon({
            html: `
              <div style="position: relative; background-color: var(--chakra-colors-blue-500); padding: 5px; border-radius: 5px; text-align: center; color: white; font-weight: bold;">
              ${getMarkerText()}
                <div style="position: absolute; left: 50%; bottom: -10px; width: 0; height: 0; border-left: 10px solid transparent; border-right: 10px solid transparent; border-top: 10px solid var(--chakra-colors-blue-500); transform: translateX(-50%);"></div>
              </div>
            `,
            className: 'custom-div-icon',
            iconSize: [60, 20],
          });
          
          return (<Marker
            key={`${hotel.key}-${index}`} // Add more specific key to force re-render
            position={[hotel.lat, hotel.lng]}
            icon={customIcon}
            eventHandlers={{
              click: () => handleMarkerClick(hotel),
            }}
          />)
        })}
        {selectedHotel && (
          <Popup
            position={[selectedHotel.lat, selectedHotel.lng]}
            onClose={() => {
              setSelectedHotel(null);
            }}
          >
            <Card
              overflow='hidden'
              align='center'
              h='100%'
            >
              <Flex direction={{ base: 'column', md: 'column' }} borderBottom={{ base: '1px', md: 'none' }} borderColor='gray.300' width='100%' align='center'>
                <Image 
                  src={imageUrl || 'https://maxmypoint.com/default-hotel-image-small.jpg'} 
                  alt={selectedHotel.name} 
                  width='100%' 
                  maxW={{ base: '100%', md: '100%' }} 
                  maxH={{ base: '150px', md: '250px' }}
                  objectFit='cover' 
                  align="center"
                  onError={() => setImageUrl('https://maxmypoint.com/default-hotel-image-small.jpg')}
                />
                <CardBody p={{ base: 3, md: 5 }} minWidth='100px'>
                  <Flex gap={{ base: 1, md: 3 }} align='center' direction='column' justify={{ base: 'center' }} height='100%'>
                    <Heading
                      size='md'
                      textAlign='center'
                      color='blue.500'
                    >
                      <ChakraLink as={RouterLink} to={`https://maxmypoint.com/hotel/${selectedHotel.key}`} target='_blank'>
                        {selectedHotel.name || 'Hotel'}
                      </ChakraLink>                    
                    </Heading>
                    <Flex direction='row' justify='space-around' width='100%' gap={3}>
                      <Stack direction='row'>
                        <Text>Total Price:</Text>
                        <Text color='blue.500' fontWeight='bold'>
                          {selectedHotel.price && typeof selectedHotel.price === 'number' && !isNaN(selectedHotel.price) ? '$' + Math.round(selectedHotel.price).toLocaleString() : 'N/A'}
                        </Text>
                      </Stack>
                      <Stack direction='row'>
                        <Text>Total Points</Text>
                        <Text color='blue.500' fontWeight='bold'>
                          {selectedHotel.points && typeof selectedHotel.points === 'number' && !isNaN(selectedHotel.points) ? (selectedHotel.points / 1000).toFixed(1) + 'K' : 'N/A'}
                        </Text>
                      </Stack>
                      <Stack direction='row'>
                        <Text>{cppLabel}</Text>
                        <Text color='blue.500' fontWeight='bold'>
                          {selectedHotel.v && typeof selectedHotel.v === 'number' && !isNaN(selectedHotel.v) ? selectedHotel.v.toFixed(1) : 'N/A'}
                        </Text>
                      </Stack>
                    </Flex>
                  </Flex>
                </CardBody>
              </Flex>
            </Card>
          </Popup>
        )}
      </MapContainer>
    </Box>
  );
};

export default Map;